import { useEffect, useMemo, useState } from "react";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Plus, Trash2, Library, BookOpen, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Courses() {
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState({ course_name: "", description: "" });
  const [expanded, setExpanded] = useState({});
  const [newModule, setNewModule] = useState({});

  const load = async () => {
    const [c, m] = await Promise.all([api.get("/courses"), api.get("/modules")]);
    setItems(c.data);
    setModules(m.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/courses", form);
    setForm({ course_name: "", description: "" });
    setOpenForm(false);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete course? Its modules will remain (clean them up manually).")) return;
    await api.delete(`/courses/${id}`);
    load();
  };

  const modulesByCourse = useMemo(() => {
    const map = {};
    for (const m of modules) (map[m.course_id] ||= []).push(m);
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return map;
  }, [modules]);

  const addModule = async (courseId) => {
    const name = (newModule[courseId] || "").trim();
    if (!name) return;
    try {
      await api.post("/modules", { name, course_id: courseId });
      setNewModule((p) => ({ ...p, [courseId]: "" }));
      load();
      toast.success("Module added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add module");
    }
  };

  const delModule = async (mid) => {
    if (!window.confirm("Delete this module?")) return;
    await api.delete(`/modules/${mid}`);
    load();
  };

  return (
    <div>
      <PageHeader
        title="Courses"
        subtitle="Define the courses your institute offers and the modules under each."
        actions={
          <button data-testid="course-add-button" onClick={() => setOpenForm(true)}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700">
            <Plus className="w-4 h-4" /> Add course
          </button>
        }
      />
      {openForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <form onSubmit={create} className="space-y-4">
            <Field label="Course name">
              <input required value={form.course_name}
                onChange={(e) => setForm({ ...form, course_name: e.target.value })}
                className={input} />
            </Field>
            <Field label="Description">
              <textarea rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className={input} />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpenForm(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
              <button data-testid="course-create-submit" type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm">Create</button>
            </div>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="No courses yet" hint="Click 'Add course' to get started." />
      ) : (
        <div className="space-y-4">
          {items.map((c) => {
            const isOpen = !!expanded[c.id];
            const mods = modulesByCourse[c.id] || [];
            return (
              <div key={c.id} data-testid={`course-card-${c.id}`}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="flex items-start gap-4 p-5">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 grid place-items-center flex-shrink-0">
                    <Library className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-lg font-semibold text-slate-900">{c.course_name}</div>
                    <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">
                      {c.description || "No description provided"}
                    </p>
                    <div className="mt-2 text-xs text-slate-500 flex items-center gap-3">
                      <span><BookOpen className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />{mods.length} module{mods.length === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                  <button onClick={() => setExpanded({ ...expanded, [c.id]: !isOpen })}
                    data-testid={`course-toggle-${c.id}`}
                    className="text-slate-500 hover:bg-slate-100 rounded-lg p-2">
                    {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>
                  <button data-testid={`course-delete-${c.id}`} onClick={() => del(c.id)}
                    className="text-red-600 hover:bg-red-50 rounded-lg p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-semibold text-slate-700">Modules</div>
                    </div>
                    {mods.length === 0 ? (
                      <div className="text-sm text-slate-500 italic mb-3">No modules yet.</div>
                    ) : (
                      <ol className="space-y-1.5 mb-3">
                        {mods.map((m) => (
                          <li key={m.id} data-testid={`module-row-${m.id}`}
                            className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2.5">
                              <span className="text-xs font-mono-acc text-slate-400 w-6 text-right">{m.order}.</span>
                              <span className="text-sm text-slate-800">{m.name}</span>
                            </div>
                            <button onClick={() => delModule(m.id)}
                              data-testid={`module-delete-${m.id}`}
                              className="text-slate-400 hover:text-red-600 rounded p-1">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ol>
                    )}
                    <form onSubmit={(e) => { e.preventDefault(); addModule(c.id); }} className="flex gap-2">
                      <input value={newModule[c.id] || ""}
                        onChange={(e) => setNewModule({ ...newModule, [c.id]: e.target.value })}
                        placeholder="New module name"
                        className={input + " flex-1"}
                        data-testid={`module-input-${c.id}`} />
                      <button type="submit" data-testid={`module-add-${c.id}`}
                        className="inline-flex items-center gap-1.5 bg-blue-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-blue-700">
                        <Plus className="w-4 h-4" /> Add
                      </button>
                    </form>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const input = "w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-900 bg-white";
function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>{children}</div>;
}
